import { useEffect } from 'react';
import i18n from 'i18n';

import { getViewerRouteTranslations } from 'services/translations';

/**
 * Loads translations for route-rendering flows where the route application/version
 * IDs are known directly from props/URL instead of Redux application state.
 */
export function useRouteTranslations(
  applicationId?: string,
  versionId?: string,
  localeCode: string = 'en-US',
) {
  useEffect(() => {
    let cancelled = false;
    if (!applicationId || !versionId || !localeCode) {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const viewerTranslations = await getViewerRouteTranslations(
          applicationId,
          localeCode,
          versionId,
        );
        const translations = {
          ...(viewerTranslations.form?.translations ?? {}),
          ...(viewerTranslations.system?.translations ?? {}),
        };

        if (cancelled) return;

        if (Object.keys(translations).length > 0) {
          const cleanTranslations = Object.fromEntries(
            Object.entries(translations).filter(
              (entry): entry is [string, string] =>
                entry[1] != null && typeof entry[1] === 'string',
            ),
          );
          i18n.addResourceBundle(localeCode, 'viewer', cleanTranslations, true, true);
        }

        const currentLng = i18n.language;
        if (currentLng === localeCode) {
          await i18n.changeLanguage('en-US');
        }
        await i18n.changeLanguage(localeCode);
      } catch {
        // Fall through to defaults when translations are unavailable.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applicationId, versionId, localeCode]);
}
